import { Links } from 'application/constants/links';
import { LexicalEditorIntegration } from './lexical/LexicalEditorIntegration';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import ErrorPage from '../../unauthorized/errorPages/ErrorPage.tsx';
import styles from './singlePage.module.scss';
import { createContext, useEffect, useMemo, useState } from 'react';
import ForbiddenErrorPage from '../../unauthorized/errorPages/ForbiddenErrorPage.tsx';
import { AxiosError } from 'axios';
import {
  CmsPageDto,
  Language,
  UserRole,
} from '../../../services/api/api-client.types.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { getCurrentSupportedLanguage } from '../../../application/localization/localization.ts';
import { useTranslation } from 'react-i18next';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import EditImage from 'assets/icons/edit.svg?react';
import { useLocation } from 'react-router';
import { CmsPageQuery, UserQuery } from 'services/api/api-client.ts';

const pageDesigner = '/page-designer';

type DefaultLanguageContextType = {
  defaultLanguage: Language | null;
};

export const DefaultLanguageContext = createContext<
  DefaultLanguageContextType | undefined
>(undefined);

export const SinglePage = () => {
  const i18n = useTranslation();
  const [defaultLanguage, setDefaultLanguage] = useState<Language | null>(null);
  const location = useLocation();
  const routeParams = Links.Authorized.CustomPages.SinglePage.useParams();
  const pageUrl = routeParams.url;

  const userQuery = UserQuery.useGetCurrentUserInfoQuery();
  const userRoles = userQuery.data?.claimRoles ?? [];
  const isAdmin = userRoles.includes(UserRole.Admin);

  const inaccessibleUrlsQuery =
    CmsPageQuery.useGetInaccessiblePublishedUrlsQuery({
      retry: false,
      throwOnError: false,
      enabled: !isAdmin,
      staleTime: 3 * 60 * 1000,
    });

  const pageQuery = CmsPageQuery.useGetDetailsByUrlQuery<
    CmsPageDto,
    AxiosError
  >(
    {
      url: pageUrl,
      getActiveVersion: false,
    },
    {
      retry: false,
      throwOnError: false,
    },
  );

  const content = useMemo(() => {
    const data = pageQuery.data;

    try {
      if (data && JSON.parse(data.content)) {
        return data.content;
      }

      return null;
    } catch (e) {
      return null;
    }
  }, [pageQuery.data?.content]);

  useEffect(() => {
    setDefaultLanguage(pageQuery.data?.defaultLanguage ?? null);
  }, [pageQuery.data?.defaultLanguage]);

  if (pageQuery.isError) {
    if (pageQuery.error?.response?.status === 403) {
      return <ForbiddenErrorPage />;
    }
    return <ErrorPage />;
  }

  const currentLanguage = getCurrentSupportedLanguage(i18n.i18n);
  const apiLanguage = (currentLanguage.at(0)?.toUpperCase() +
    currentLanguage.slice(1)) as Language;
  const translatedTitle = pageQuery.data?.titleTranslations.find(
    (translation) => translation.language === apiLanguage,
  );

  if (!pageQuery.isFetchedAfterMount || inaccessibleUrlsQuery.isLoading) {
    return <Loading loading={true} />;
  }

  return (
    <div>
      {!pageQuery.isLoading && (
        <Breadcrumbs
          pageName={translatedTitle?.translatedText ?? pageQuery.data?.title}
        >
          {isAdmin && (
            <ActionText
              text={i18n.t('edit_in_page_designer')}
              Svg={EditImage}
              onClickHandler={() =>
                window.open(`${pageDesigner}${location.pathname}`, '_blank')
              }
            />
          )}
        </Breadcrumbs>
      )}
      <DefaultLanguageContext.Provider
        value={{
          defaultLanguage,
        }}
      >
        <div className={styles.container}>
          <LexicalEditorIntegration
            isPageDesigner={false}
            initialState={content}
            editable={false}
            key={pageUrl.toString() + (pageQuery.data === undefined ? 0 : 1)}
          />
        </div>
      </DefaultLanguageContext.Provider>
    </div>
  );
};
